import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Activity,
  Landmark,
  ShieldCheck,
  Eye,
  Layers,
  Globe2,
} from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { LiveXauusdPrice } from "@/components/site/LiveXauusdPrice";

const SITE = "https://xauproacademy.com";
const PAGE_URL = `${SITE}/xauusd-trading`;

const XAUUSD_CURRICULUM = [
  { step: "01", layer: "Timing", slug: "gold-trading-sessions-london-new-york", title: "Gold trading sessions: London and New York", body: "When XAUUSD moves, why London sweeps and New York continues, and which hours to avoid." },
  { step: "02", layer: "Daily cycle", slug: "amd-pattern-gold-daily-cycles", title: "The AMD pattern in gold", body: "Accumulation, manipulation and distribution across the gold trading day." },
  { step: "03", layer: "Liquidity", slug: "liquidity-sweep-gold-trading", title: "Liquidity sweeps in gold", body: "How stop runs above highs and below lows set up XAUUSD reversals and continuations." },
  { step: "04", layer: "Location", slug: "premium-discount-zones-gold", title: "Premium and discount zones", body: "Buying low and selling high inside the active XAUUSD dealing range." },
  { step: "05", layer: "POI", slug: "order-blocks-fvg-explained", title: "Order blocks and fair value gaps", body: "Qualifying the zones institutions defend, and the imbalances price returns to." },
  { step: "06", layer: "POI", slug: "breaker-block-trading-gold", title: "Breaker blocks in gold", body: "Failed order blocks that flip role after a structure break and a sweep." },
  { step: "07", layer: "Entry", slug: "high-probability-entry-criteria-gold", title: "High-probability entry criteria", body: "The confluence checklist an XAUUSD setup must pass before risk is committed." },
  { step: "08", layer: "Strategy", slug: "smc-gold-trading-strategy", title: "SMC gold trading strategy", body: "Putting structure, liquidity and risk together into one XAUUSD execution process." },
] as const;

/**
 * Pillar 5: XAUUSD Entity.
 *
 * Top-of-funnel entity destination for the XAUUSD long-tail. The page is
 * definitional, but Orion as a live institutional read distinguishes it
 * from generic broker explainer pages. Trust Chain handoffs land on
 * /gold-analysis (recurring), /signals (decisions), /orion (engine),
 * /validation (audit) and /pricing.
 */
export const Route = createFileRoute("/xauusd-trading")({
  head: () => ({
    meta: [
      { title: "How to Trade Gold (XAUUSD): Institutional SMC Guide | XAU Pro Academy" },
      {
        name: "description",
        content:
          "How to trade gold step by step. What XAUUSD is, contract size and pip value, how gold differs from forex, the best sessions, and the institutional SMC process for entries and risk.",
      },
      {
        name: "keywords",
        content:
          "how to trade gold, how to trade xauusd, what is xauusd, gold trading for beginners, xauusd trading strategy, gold contract size, gold pip value, gold vs forex, xauusd trading, xauusd vs gold futures, how to trade xauusd, xauusd analysis, institutional xauusd, xauusd price",
      },
      { property: "og:title", content: "How to Trade Gold (XAUUSD): Institutional SMC Guide" },
      {
        property: "og:description",
        content:
          "What XAUUSD is, contract and pip math, sessions, liquidity and risk: the complete institutional guide to trading gold.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "XAUUSD",
          url: PAGE_URL,
          hasDefinedTerm: [
            {
              "@type": "DefinedTerm",
              name: "XAUUSD",
              alternateName: ["Gold spot", "Spot gold", "Gold vs US Dollar"],
              description:
                "XAUUSD is the price of one troy ounce of gold quoted in United States dollars on the spot foreign exchange market. The ticker uses the ISO 4217 commodity code XAU for gold paired against USD.",
              url: PAGE_URL,
              inDefinedTermSet: PAGE_URL,
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How do you trade gold?",
              acceptedAnswer: { "@type": "Answer", text: "Trade gold through spot XAUUSD with a regulated broker or a prop firm account. Define the higher timeframe bias on H4 and H1, wait for a liquidity sweep during the London or New York session, confirm displacement, enter from a qualified point of interest, and size the position so the stop costs a fixed fraction of capital." },
            },
            {
              "@type": "Question",
              name: "What is the contract size for gold?",
              acceptedAnswer: { "@type": "Answer", text: "At most brokers one standard lot of XAUUSD equals 100 troy ounces, a mini lot 10 ounces and a micro lot 1 ounce. Check your broker's contract specification, as some use different sizes." },
            },
            {
              "@type": "Question",
              name: "How much is a 1 dollar move in gold worth?",
              acceptedAnswer: { "@type": "Answer", text: "With a 100 ounce standard lot, a 1.00 move in XAUUSD is worth $100 per lot, $10 on 0.10 lots and $1 on 0.01 lots. Pip definitions vary by broker, so dollar distance is the safest unit for risk." },
            },
            {
              "@type": "Question",
              name: "What is XAUUSD?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "XAUUSD is the spot price of one troy ounce of gold quoted in US dollars. XAU is the ISO 4217 code for gold; USD is the US dollar. The pair trades on the over-the-counter spot foreign exchange market, not on a centralised futures exchange.",
              },
            },
            {
              "@type": "Question",
              name: "How is XAUUSD different from gold futures?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "XAUUSD is a spot OTC instrument with no expiry, settled at the prevailing dealer price. Gold futures (such as COMEX GC) are exchange-listed contracts with fixed expiries, standardised lot sizes, and exchange margin rules. XAUUSD allows continuous spot exposure; futures introduce roll and settlement mechanics.",
              },
            },
            {
              "@type": "Question",
              name: "Why do institutional desks watch XAUUSD?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "XAUUSD is the cleanest read on gold demand outside of the futures settlement cycle. Real yields, US dollar strength, central bank reserve policy, and geopolitical risk all express themselves in spot gold before they reach derived markets.",
              },
            },
            {
              "@type": "Question",
              name: "How does XAU Pro Academy analyse XAUUSD?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "Through the Orion decision engine and the team analyst desk. Both apply Smart Money Concepts (market structure, liquidity, displacement, point-of-interest qualification) on H4 down to M15, with a macro overlay. Every decision is published with full reasoning to the validation log.",
              },
            },
            {
              "@type": "Question",
              name: "What is the best time to trade XAUUSD?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "XAUUSD is most active during the London open, the New York open and the London and New York overlap. Asian session conditions are typically lower liquidity and require more caution. Many institutional setups form after a London liquidity sweep with New York continuation.",
              },
            },
            {
              "@type": "Question",
              name: "How do I start trading XAUUSD?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "Start by understanding session timing, liquidity, market structure and risk per trade. Define invalidation before entry, size positions so a stop costs a fixed fraction of capital, and avoid trading during unstable high-impact news. The XAU Pro Academy curriculum covers each layer for free.",
              },
            },
            {
              "@type": "Question",
              name: "Are XAUUSD signals reliable?",
              acceptedAnswer: {
                "@type": "Answer",
                text:
                  "Only when every trade, including losses, is recorded with entry, stop and targets before the outcome is known. XAU Pro Academy publishes an append-only trade ledger and a public validation hub so results can be checked rather than trusted.",
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "XAUUSD execution curriculum",
          itemListElement: XAUUSD_CURRICULUM.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.title,
            url: `${SITE}/education/${item.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "How to Trade Gold (XAUUSD): Institutional SMC Guide",
          description: "Ways to trade gold, what moves the gold price, contract and dollar risk math, sessions and a five step institutional process.",
          url: PAGE_URL,
          mainEntityOfPage: PAGE_URL,
          dateModified: "2026-10-16",
          author: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
          about: ["Gold trading", "XAUUSD", "Gold futures", "Risk management"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "How to Trade Gold", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  component: XauusdTradingPage,
});

function XauusdTradingPage() {
  return (
    <Layout>
      {/* Entity hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[32rem] w-[56rem] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, hsl(var(--gold) / 0.45), transparent)" }}
        />
        <div className="container relative mx-auto max-w-7xl px-4 pb-20 pt-20 md:pt-28">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
              The Institutional SMC Guide
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
              How to Trade <span className="text-gradient-gold">Gold (XAUUSD)</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
              The spot price of one troy ounce of gold quoted in US dollars. The
              cleanest live read on global gold demand. The instrument the Orion
              decision engine and the XAU Pro analyst team govern every day.
            </p>
            <div className="mt-8 flex justify-center">
              <LiveXauusdPrice />
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="gold" size="lg">
                <Link to="/gold-analysis">Latest XAUUSD reads</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/orion">How Orion reads XAUUSD</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Direct answer */}
      <section id="quick-answer" className="border-b border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Short answer</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">How do you trade gold?</h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/90 md:text-lg">
            <strong>
              To trade gold, choose an access route (spot XAUUSD through a regulated broker, gold
              futures on COMEX, or a gold ETF), learn what moves the price (real yields, the US
              dollar, inflation data and central bank demand), trade during the London and New York
              sessions, define your stop before entry and size the position so one loss costs a
              small, fixed fraction of capital, typically 0.5% to 1%.
            </strong>
          </p>
          <div className="mt-8 overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full text-left text-sm">
              <thead className="bg-card/80 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <tr><th className="p-3">Step</th><th className="p-3">Decision</th><th className="p-3">Where to go next</th></tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-muted-foreground">
                <tr><td className="p-3 font-mono text-gold">1</td><td className="p-3">Pick how you access gold</td><td className="p-3"><a href="#ways-to-trade" className="text-gold hover:underline">Ways to trade gold</a></td></tr>
                <tr><td className="p-3 font-mono text-gold">2</td><td className="p-3">Understand the price drivers</td><td className="p-3"><a href="#drivers" className="text-gold hover:underline">What moves the gold price</a></td></tr>
                <tr><td className="p-3 font-mono text-gold">3</td><td className="p-3">Learn contract size and dollar risk</td><td className="p-3"><a href="#contract" className="text-gold hover:underline">Contract size and pip value</a></td></tr>
                <tr><td className="p-3 font-mono text-gold">4</td><td className="p-3">Follow a repeatable process</td><td className="p-3"><a href="#how-to-trade" className="text-gold hover:underline">Five step process</a></td></tr>
                <tr><td className="p-3 font-mono text-gold">5</td><td className="p-3">Fix risk on every trade</td><td className="p-3"><a href="#risk-example" className="text-gold hover:underline">Worked risk example</a></td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Ways to trade gold */}
      <section id="ways-to-trade" className="border-b border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Access routes</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Ways to trade gold</h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Gold can be held, invested in or actively traded. Each route carries different costs,
            leverage, regulation and tax treatment depending on your country. Short term traders
            mostly use spot XAUUSD or futures.
          </p>
          <div className="mt-8 overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-card/80 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <tr><th className="p-3">Route</th><th className="p-3">Best suited to</th><th className="p-3">Leverage</th><th className="p-3">Main consideration</th></tr>
              </thead>
              <tbody className="divide-y divide-border/60 text-muted-foreground">
                <tr><td className="p-3 font-semibold text-foreground">Spot XAUUSD (CFD or spot FX)</td><td className="p-3">Intraday and swing trading</td><td className="p-3">Yes, broker and region dependent</td><td className="p-3">Spread, overnight swap, not available as CFD to US retail clients</td></tr>
                <tr><td className="p-3 font-semibold text-foreground">Gold futures (COMEX GC, MGC)</td><td className="p-3">Exchange traded exposure, US traders</td><td className="p-3">Exchange margin</td><td className="p-3">Contract expiry and roll, whole contract sizing</td></tr>
                <tr><td className="p-3 font-semibold text-foreground">Gold ETFs</td><td className="p-3">Longer term investing</td><td className="p-3">None by default</td><td className="p-3">Management fee, trades only during exchange hours</td></tr>
                <tr><td className="p-3 font-semibold text-foreground">Physical bullion</td><td className="p-3">Wealth preservation</td><td className="p-3">None</td><td className="p-3">Dealer premium, storage and insurance</td></tr>
                <tr><td className="p-3 font-semibold text-foreground">Gold mining shares</td><td className="p-3">Equity investors</td><td className="p-3">None by default</td><td className="p-3">Company risk on top of the gold price</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Region specific guides: <Link to="/xauusd-trading/usa" className="text-gold hover:underline">how to trade gold in the USA</Link> and <Link to="/xauusd-trading/uk" className="text-gold hover:underline">how to trade gold in the UK</Link>.
          </p>
        </div>
      </section>

      {/* Drivers */}
      <section id="drivers" className="border-b border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Fundamentals</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">What moves the gold price</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              { t: "Real interest rates", b: "Gold pays no yield. When inflation adjusted Treasury yields fall, the cost of holding gold falls and demand tends to rise. Rising real yields usually weigh on price." },
              { t: "The US dollar", b: "Gold is priced in dollars, so a weaker dollar generally supports XAUUSD and a stronger dollar tends to pressure it. The relationship is strong but not constant." },
              { t: "Inflation and jobs data", b: <>CPI, PPI and Nonfarm Payrolls reprice rate expectations within seconds. Follow them on the <Link to="/gold-inflation-cpi" className="text-gold hover:underline">CPI and gold</Link> and <Link to="/gold-nfp-employment" className="text-gold hover:underline">NFP and gold</Link> pages.</> },
              { t: "Central bank and Fed policy", b: "FOMC decisions set the rate path, while central bank reserve buying adds structural demand that can hold prices up even when yields rise." },
              { t: "Risk sentiment and geopolitics", b: "Gold acts as a hedge during stress. Conflict, banking strain or equity sell offs can lift demand quickly, then fade once the shock is priced." },
              { t: "Positioning", b: <>Large speculators and commercial hedgers report weekly in the <Link to="/gold-cot-report" className="text-gold hover:underline">gold COT report</Link>, a useful read on crowded positioning.</> },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-border/60 bg-card/60 p-6">
                <div className="font-display text-lg font-bold">{c.t}</div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is XAUUSD */}
      <section id="what" className="border-b border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Definition
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">What is XAUUSD?</h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            <p>
              XAUUSD is the spot price of one troy ounce of gold quoted in US
              dollars. XAU is the ISO 4217 commodity code for gold. USD is the
              United States dollar. The pair trades on the over-the-counter spot
              foreign exchange market and has no expiry.
            </p>
            <p>
              A move of 1.00 in XAUUSD represents a one-dollar change in the
              price of a troy ounce of gold. Spot pricing reflects current
              dealer bids and offers across the global interbank market, updated
              continuously through the trading week.
            </p>
          </div>
        </div>
      </section>

      {/* Why traders trade XAUUSD */}
      <section className="border-b border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
            Why XAUUSD
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
            Why traders trade XAUUSD
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              {
                icon: Globe2,
                label: "Continuous spot exposure",
                body: "Spot gold trades 23 hours a day from Sunday open to Friday close, with no contract expiry or roll mechanics. Positions reflect the live institutional bid.",
              },
              {
                icon: Activity,
                label: "Volatility with structure",
                body: "Gold expresses macro stress (real yields, dollar, geopolitics) cleanly. Sessions are highly structured: Asia consolidation, London expansion, New York continuation or reversal.",
              },
              {
                icon: Landmark,
                label: "Macro alignment",
                body: "Real yields, US dollar strength, central bank reserve flow, and risk-off behaviour each leave a clear signature in XAUUSD before they appear in derived markets.",
              },
              {
                icon: Layers,
                label: "Liquidity behaviour",
                body: "Gold is a deeply traded global asset. Smart Money Concepts (liquidity sweeps, displacement, point-of-interest mitigation) work cleanly on XAUUSD because the order flow is real.",
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-xl border border-border/60 bg-card/60 p-6 backdrop-blur"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <card.icon className="h-4 w-4" />
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                  {card.label}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* XAUUSD vs Gold Futures */}
      <section id="vs-futures" className="border-b border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Instrument comparison
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
            How XAUUSD differs from gold futures
          </h2>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground">
            XAUUSD and exchange-listed gold futures (COMEX GC most notably) both
            give exposure to the gold price. The mechanics, the liquidity
            profile, and the trader experience are not the same.
          </p>

          <div className="mt-8 overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="bg-card/60">
                  <th className="p-4 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Dimension
                  </th>
                  <th className="p-4 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-gold">
                    XAUUSD (spot)
                  </th>
                  <th className="p-4 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    Gold futures (COMEX GC)
                  </th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                {[
                  ["Venue", "Over-the-counter spot FX", "Centralised futures exchange"],
                  ["Expiry", "No expiry, perpetual spot", "Fixed expiry, periodic roll"],
                  ["Unit", "1 troy ounce", "100 troy ounces per contract"],
                  ["Pricing", "Live dealer bid and offer", "Exchange settlement and last traded"],
                  ["Margin", "Broker-set FX margin", "Exchange-set futures margin"],
                  ["Trading hours", "Sun open to Fri close, 23h per day", "Exchange hours with electronic session"],
                  ["Typical user", "Retail and prop traders, institutional FX desks", "Institutional hedgers, funds, professional traders"],
                ].map(([k, a, b]) => (
                  <tr key={k} className="border-t border-border/60">
                    <td className="p-4 font-medium text-foreground">{k}</td>
                    <td className="p-4">{a}</td>
                    <td className="p-4">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            The platform analyses XAUUSD spot because the read is continuous,
            the liquidity is real, and the SMC framework expresses itself
            cleanly without the distortion of expiry mechanics.
          </p>
        </div>
      </section>

      {/* Contract specs */}
      <section id="contract" className="border-b border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Contract math</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Gold contract size and pip value</h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Most brokers quote one standard lot of XAUUSD as 100 troy ounces. Every 1.00 move in price is therefore worth $100 per standard lot.
            Pip definitions differ between brokers (some use $0.10, some $0.01), so the desk measures risk in dollar distance, not pips.
          </p>
          <div className="mt-6 overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-card/60 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <tr><th className="p-4 text-left">Lot size</th><th className="p-4 text-left">Ounces</th><th className="p-4 text-left">Value of a $1.00 move</th><th className="p-4 text-left">Value of a $20 stop</th></tr>
              </thead>
              <tbody className="text-muted-foreground">
                {[["1.00 standard","100","$100","$2,000"],["0.10 mini","10","$10","$200"],["0.01 micro","1","$1","$20"]].map(([a,b,c,d]) => (
                  <tr key={a} className="border-t border-border/60"><td className="p-4 font-medium text-foreground">{a}</td><td className="p-4">{b}</td><td className="p-4 text-gold">{c}</td><td className="p-4">{d}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <Link to="/position-size-calculator" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-gold">
            Calculate your exact lot size <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* Gold vs forex */}
      <section id="gold-vs-forex" className="border-b border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Instrument behaviour</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">How trading gold differs from forex</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {[
              { t: "Wider daily ranges", b: "Gold routinely moves tens of dollars a day. Stops sized for currency pairs are too tight, so position size must shrink as stop distance grows." },
              { t: "Driven by real yields and the dollar", b: "Falling real yields and a weaker dollar tend to support gold. Macro releases like CPI and NFP move it harder than most currency pairs." },
              { t: "Institutional positioning matters", b: "Commercial hedgers and managed money leave a weekly footprint in the COT report that frames the higher timeframe narrative." },
              { t: "Session concentration", b: "Most of the daily range is delivered between the London open and the New York overlap. Asian hours usually build the range London later sweeps." },
            ].map((c) => (
              <div key={c.t} className="rounded-xl border border-border/60 bg-card/60 p-6">
                <div className="font-display text-lg font-bold">{c.t}</div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.b}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Track the drivers directly: <Link to="/gold-cot-report" className="text-gold hover:underline">gold COT report</Link>, <Link to="/gold-inflation-cpi" className="text-gold hover:underline">CPI and gold</Link>, <Link to="/gold-nfp-employment" className="text-gold hover:underline">NFP and gold</Link> and <Link to="/gold-seasonality" className="text-gold hover:underline">seasonality XAUUSD</Link>.
          </p>
        </div>
      </section>

      {/* Step by step process */}
      <section id="how-to-trade" className="border-b border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Execution process</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">How to trade gold in five steps</h2>
          <ol className="mt-8 space-y-5">
            {[
              { n: "01", t: "Set the higher timeframe bias", b: <>Read structure on H4, then H1. Only a candle body close beyond structure with displacement counts as a break. See <Link to="/education/$slug" params={{ slug: "choch-vs-bos-smart-money" }} className="text-gold hover:underline">CHoCH vs BOS</Link>.</> },
              { n: "02", t: "Trade the liquid session", b: <>Focus on the London open and the London and New York overlap. Check the live <Link to="/gold-trading-sessions" className="text-gold hover:underline">gold trading hours clock</Link> for your time zone.</> },
              { n: "03", t: "Wait for a liquidity sweep", b: <>Let price take the Asian range or a prior session high or low first. Learn the pattern in <Link to="/education/$slug" params={{ slug: "liquidity-sweep-gold-trading" }} className="text-gold hover:underline">liquidity sweeps in gold</Link>.</> },
              { n: "04", t: "Enter from a qualified POI", b: <>After displacement, enter from a fresh <Link to="/education/$slug" params={{ slug: "order-blocks-fvg-explained" }} className="text-gold hover:underline">order block or fair value gap</Link> in discount for longs or premium for shorts.</> },
              { n: "05", t: "Fix risk before entry", b: <>Place the stop beyond the structure that invalidates the idea, then size the trade with the <Link to="/position-size-calculator" className="text-gold hover:underline">gold position size calculator</Link>. Skip the trade inside high impact news windows.</> },
            ].map((s) => (
              <li key={s.n} className="flex gap-5 rounded-xl border border-border/60 bg-card/60 p-6">
                <div className="font-mono text-sm text-gold">{s.n}</div>
                <div>
                  <div className="font-display text-lg font-bold">{s.t}</div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Worked risk example */}
      <section id="risk-example" className="border-b border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Capital preservation</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Worked example: sizing a gold trade</h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Risk is set in dollars before the entry, never after. A trader with a $10,000 account
            risking 1% per trade has $100 at risk. If the invalidation sits $10 away from entry,
            each 0.01 lot (1 ounce) loses $10 at the stop, so the correct size is 0.10 lots.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-4">
            {[
              { k: "Account", v: "$10,000" },
              { k: "Risk per trade", v: "1% = $100" },
              { k: "Stop distance", v: "$10.00" },
              { k: "Position size", v: "0.10 lots" },
            ].map((c) => (
              <div key={c.k} className="rounded-xl border border-border/60 bg-card/60 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{c.k}</div>
                <div className="mt-2 font-display text-xl font-bold">{c.v}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Formula: position size in lots equals dollar risk divided by (stop distance in dollars
            times 100). Run your own numbers with the <Link to="/position-size-calculator" className="text-gold hover:underline">gold position size calculator</Link>.
          </p>
        </div>
      </section>

      {/* Prop firm */}
      <section id="prop-firm" className="border-b border-border/60">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Funded accounts</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Trading gold on a prop firm account</h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">
            Gold's volatility makes it the fastest way to pass an evaluation and the fastest way to fail one. The common breaches are news slippage past the daily loss limit, wide spreads at the Asian reopen, and trailing drawdown that locks in after a winning run.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="outline"><Link to="/best-prop-firms-for-gold">Best prop firms for gold</Link></Button>
            <Button asChild variant="outline"><Link to="/prop-firm-gold">Prop firm gold guide</Link></Button>
          </div>
        </div>
      </section>

      {/* Tools grid */}
      <section className="border-b border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-7xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Free gold trading tools</div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Put the guide to work</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { to: "/position-size-calculator" as const, t: "Gold lot size calculator", b: "Lot size from account, risk and stop distance." },
              { to: "/gold-trading-sessions" as const, t: "Gold trading hours", b: "Live session clock and news freeze windows." },
              { to: "/gold-cot-report" as const, t: "Gold COT report", b: "Weekly institutional positioning in gold futures." },
              { to: "/xauusd-analysis-today" as const, t: "XAUUSD analysis today", b: "Live price, sessions and today's market context." },
              { to: "/smart-money-concepts" as const, t: "Smart Money Concepts", b: "The framework behind every step in this guide." },
              { to: "/signals" as const, t: "Live desk signals", b: "Qualified setups with grade and reasoning." },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="group rounded-xl border border-border/60 bg-card p-5 transition hover:border-gold/40">
                <div className="font-medium text-foreground group-hover:text-gold">{c.t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{c.b}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Institutional view */}
      <section className="border-b border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-5xl px-4 py-16">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
            Institutional perspective
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
            How institutional desks read XAUUSD
          </h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {[
              {
                step: "01",
                label: "Macro layer first",
                body: "Real yields, US dollar trajectory, policy posture, and event risk define whether bias is permitted at all. Macro instability reduces conviction; macro alignment raises it.",
              },
              {
                step: "02",
                label: "Structure and liquidity",
                body: "Higher-timeframe bias from H4 and H1. Liquidity sweeps before entry confirmation. Displacement before bias change. M15 executes; it never decides.",
              },
              {
                step: "03",
                label: "Selectivity over frequency",
                body: "Low-quality conditions are rejected. Neutral bias is a valid output. Frequency is never optimised at the expense of capital.",
              },
            ].map((card) => (
              <div key={card.step} className="rounded-xl border border-border/60 bg-card/60 p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {card.step}
                </div>
                <div className="mt-2 font-display text-lg font-bold">{card.label}</div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* XAUUSD execution curriculum (topic cluster) */}
      <section id="curriculum" className="border-b border-border/60">
        <div className="container mx-auto max-w-7xl px-4 py-16">
          <div className="max-w-3xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
              XAUUSD execution curriculum
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
              How to trade XAUUSD: the institutional concepts
            </h2>
            <p className="mt-4 text-muted-foreground">
              Knowing what XAUUSD is comes first. Reading it correctly is a
              sequence: session timing, liquidity, displacement, and a
              qualified point of interest. Each guide below covers one layer,
              applied to gold. All are free to read.
            </p>
            <Link to="/gold-trading-strategy" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:underline">
              Read the complete Gold Trading Strategy playbook <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {XAUUSD_CURRICULUM.map((item) => (
              <Link
                key={item.slug}
                to="/education/$slug"
                params={{ slug: item.slug }}
                className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {item.step} · {item.layer}
                </div>
                <div className="mt-2 font-display text-lg font-bold">{item.title}</div>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{item.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">
                  Read guide <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/education" className="inline-flex items-center gap-1 text-sm font-semibold text-gold">
              Browse the full gold trading library <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Chain handoff */}
      <section id="trust-chain" className="border-b border-border/60">
        <div className="container mx-auto max-w-7xl px-4 py-16">
          <div className="mx-auto max-w-3xl text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
              From entity to decision
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">
              XAUUSD is the asset. The Trust Chain is the platform.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Every surface that follows reads the same XAUUSD price stream you
              see above, governed by the same decision engine and the same
              public audit.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                to: "/gold-analysis" as const,
                icon: Activity,
                label: "Gold Analysis",
                body: "Recurring institutional XAUUSD reads: weekly outlook, daily structure, macro overlay.",
              },
              {
                to: "/orion" as const,
                icon: Landmark,
                label: "Orion Engine",
                body: "The SMC and ICT decision engine reading XAUUSD live with full reasoning.",
              },
              {
                to: "/signals" as const,
                icon: Eye,
                label: "Published Decisions",
                body: "The live decision stream from the team and Orion, published with grade and reasoning.",
              },
              {
                to: "/validation" as const,
                icon: ShieldCheck,
                label: "Validation Hub",
                body: "How Orion is validated against historical XAUUSD data, in public.",
              },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
                  <c.icon className="h-5 w-5" />
                </div>
                <div className="mt-4 font-display text-lg font-bold">{c.label}</div>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{c.body}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">
                  Open <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Jurisdictional sub-pages */}
      <section className="border-b border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-5xl px-4 py-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Jurisdictional guides
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold md:text-3xl">
            How to trade XAUUSD, by jurisdiction
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Regulatory framing and access mechanics differ by country. These
            pages explain the practical landscape without recommending a
            specific broker.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Link
              to="/xauusd-trading/usa"
              className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                United States
              </div>
              <div className="mt-2 font-display text-lg font-bold">
                How to trade XAUUSD in the USA
              </div>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">
                What US traders can and cannot access on spot gold, the role of
                CFTC and NFA regulation, and the practical alternatives.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">
                Read guide{" "}
                <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
              </span>
            </Link>
            <Link
              to="/xauusd-trading/uk"
              className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 transition hover:border-gold/40"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                United Kingdom
              </div>
              <div className="mt-2 font-display text-lg font-bold">
                How to trade XAUUSD in the UK
              </div>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">
                What UK traders can access on spot gold under the FCA framework,
                including spread bets, CFDs and prop firm participation.
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">
                Read guide{" "}
                <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section>
        <div className="container mx-auto max-w-5xl px-4 py-16 text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">
            Read XAUUSD the way an institutional desk does
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Join the platform that publishes every XAUUSD decision with full
            reasoning, validated against the historical record.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/pricing">Membership options</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/validation">See the validation</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
